import { useState } from "react";
import React, { forwardRef } from "react";
import { SendPartnerFormData } from "../../Services/partnerService.js";
import Button from "../ui/Button/Button.jsx";
import { FormField } from "../ui/Form";
import { useButtonLoading } from "../../Hooks/useButtonLoading.js";
import { useFormValidation } from "../../Hooks/useFormValidation";
import { partnerSchema } from "../../schemas/partnerSchema";
import { useTranslation } from "react-i18next";

const FinalSection = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyEmail: "",
    companyUrl: "",
    details: "",
  });

  const [formError, setFormError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { errors, validate, validateField } = useFormValidation(partnerSchema);

  const { isLoading, buttonText, executeAsync } = useButtonLoading(
    t("common.submit"),
    t("common.submitting")
  );

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      validateField(field, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationResult = validate(formData);

    if (!validationResult.success) {
      setFormError(t("common.fixFormErrors"));
      return;
    }

    setFormError("");

    await executeAsync(
      async () => {
        const response = await SendPartnerFormData(formData);

        if (response.status === 200) {
          setFormData({
            firstName: "",
            lastName: "",
            companyEmail: "",
            companyUrl: "",
            details: "",
          });
          setIsSubmitted(true);

          setTimeout(() => {
            setIsSubmitted(false);
          }, 1500);
        } else {
          throw new Error("Failed to submit form data");
        }
      },
      {
        onError: (error) => {
          setFormError(t("common.submitFailed"));
          setIsSubmitted(false);
        }
      }
    );
  };

  return (
    <section className="w-full py-12 md:py-24 lg:py-32 bg-red-400 rounded-t-[20px] ">
      <div className="container mx-auto px-4 md:px-6 flex flex-col lg:flex-row gap-8">
        <div className="lg:w-1/2">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-grey-500">
            {t("partner.joinEcosystemTitle")}
          </h2>
          <p className="text-lg text-grey-500 mt-4">
            {t("partner.joinEcosystemDesc")}
          </p>
        </div>

        <div
          ref={ref}
          className="bg-white rounded-lg shadow-md p-6 space-y-6 max-w-xl w-full"
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <FormField
              label={t("forms.firstName")}
              htmlFor="firstName"
              required
              error={errors.firstName}
              inputProps={{
                id: "firstName",
                placeholder: t("persona.placeholderName"),
                value: formData.firstName,
                onChange: (e) => handleChange("firstName", e.target.value),
                variant: "box",
              }}
            />
            <FormField
              label={t("forms.lastName")}
              htmlFor="lastName"
              required
              error={errors.lastName}
              inputProps={{
                id: "lastName",
                placeholder: t("forms.lastName"),
                value: formData.lastName,
                onChange: (e) => handleChange("lastName", e.target.value),
                variant: "box",
              }}
            />
            <FormField
              label={t("forms.companyEmail")}
              htmlFor="companyEmail"
              required
              error={errors.companyEmail}
              inputProps={{
                id: "companyEmail",
                type: "email",
                placeholder: "example@company.com",
                value: formData.companyEmail,
                onChange: (e) => handleChange("companyEmail", e.target.value),
                variant: "box",
              }}
            />
            <FormField
              label={t("forms.companyUrl")}
              htmlFor="companyUrl"
              required
              error={errors.companyUrl}
              inputProps={{
                id: "companyUrl",
                type: "url",
                placeholder: "https://www.company.com",
                value: formData.companyUrl,
                onChange: (e) => handleChange("companyUrl", e.target.value),
                variant: "box",
              }}
            />
            <FormField
              label={t("forms.additionalDetails")}
              htmlFor="details"
              type="textarea"
              error={errors.details}
              inputProps={{
                id: "details",
                placeholder: t("forms.detailsPlaceholder"),
                value: formData.details,
                onChange: (e) => handleChange("details", e.target.value),
                variant: "box",
                rows: 4,
              }}
            />

            <Button
              type="submit"
              variant="danger"
              size="md"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
            >
              {buttonText}
            </Button>
            {formError && <p className="text-red-500 text-sm">{formError}</p>}
            {isSubmitted && (
              <p className="text-green-500 text-sm">
                {t("auth.registerSuccess")}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
});

export default FinalSection;
